import { useQuery } from '@apollo/client/react';

import {
  RESOURCE_ACCESS_QUERY,
  type ResourceAccessResult,
  type ResourceAccessVariables,
} from '@/graphql/permissions';

export type AccessState = 'checking' | 'none' | 'read' | 'full' | 'unavailable';

/**
 * What Keystone says this login may do with a platform resource.
 *
 * Asked once per page load: a role an admin unassigns is gone on the next
 * load with no deploy. `unavailable` is kept apart from `none` — an engine
 * that could not answer is not a refusal, and saying "no access" would send
 * someone to an admin for a grant they already hold.
 */
export function useResourceAccess(
  resource: string,
  { skip = false }: { skip?: boolean } = {},
): {
  access: AccessState;
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useQuery<ResourceAccessResult, ResourceAccessVariables>(
    RESOURCE_ACCESS_QUERY,
    { variables: { resource }, skip },
  );

  let access: AccessState;
  if (skip || (loading && !data)) {
    access = 'checking';
  } else if (error || !data) {
    access = 'unavailable';
  } else if (data.full?.allowed === true) {
    access = 'full';
  } else if (data.read?.allowed === true) {
    access = 'read';
  } else if (data.read?.status === 'UNAVAILABLE') {
    access = 'unavailable';
  } else {
    access = 'none';
  }

  return { access, refetch: () => void refetch() };
}
